import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { BasketIcon, BasketPopup } from '../Basket/Basket';
import { config } from '../../config';
import styles from './Navigation.module.css';

export const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useSiteSettings();
  const { isAuthenticated, logout } = useCustomerAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const basketRef = useRef<HTMLDivElement>(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Toggle a `is-scrolled` class on the header once the page is scrolled down.
  // Themes opt in to a condensed header by styling `header.is-scrolled`
  // (e.g. shaperoom shrinks the logo + top bar on desktop).
  //
  // Uses hysteresis (collapse at >80px, expand only back below <30px) rather
  // than a single threshold. Condensing the header reflows the page, which
  // moves the scroll position; a single threshold would flip the class on/off
  // repeatedly around that point and make the header visibly shake.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(prev => {
        if (!prev && y > 80) return true;
        if (prev && y < 30) return false;
        return prev;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (basketRef.current && !basketRef.current.contains(event.target as Node)) {
        setBasketOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build navigation items from CMS; empty array if none configured
  const navItems = settings.navigation.map(item => ({
    label: item.label,
    href: item.url,
    isExternal: item.openInNewTab,
    children: item.children || [],
  }));

  const isInternal = (href: string) => href.startsWith('/') && !href.includes('#');

  return (
    <>
    <header className={`${styles.header}${scrolled ? ' is-scrolled' : ''}`}>
    {settings.showHeaderTopBar && (settings.address || settings.phone || settings.email || settings.facebookUrl || settings.instagramUrl) && (
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarContact}>
            {settings.address && (
              <span className={styles.topBarItem}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                {settings.address}
              </span>
            )}
            {settings.phone && (
              <a href={`tel:${settings.phone.replace(/\s/g, '')}`} className={styles.topBarItem}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 256 256"><path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L143.16,155A102,102,0,0,1,101,112.84l15.77-16.05,0,0a16,16,0,0,0,1.94-15.91l-21.11-47.11A16,16,0,0,0,82.59,24a16.14,16.14,0,0,0-5.18.86L38.19,39.51a16.11,16.11,0,0,0-11,14.07c-.11,1.35-.16,2.7-.16,4.06,0,103,83.66,186.7,186.69,186.7,1.36,0,2.71,0,4.06-.16a16.11,16.11,0,0,0,14.07-11l14.65-39.22A16.17,16.17,0,0,0,222.37,158.46Z"></path></svg>
                {settings.phone}
              </a>
            )}
            {settings.email && (
              <a href={`mailto:${settings.email}`} className={styles.topBarItem}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                {settings.email}
              </a>
            )}
          </div>
          {(settings.facebookUrl || settings.instagramUrl) && (
            <div className={styles.topBarSocial}>
              {settings.facebookUrl && (
                <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" className={styles.topBarSocialLink} aria-label="Facebook">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"></path></svg>
                </a>
              )}
              {settings.instagramUrl && (
                <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className={styles.topBarSocialLink} aria-label="Instagram">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path></svg>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    <nav className={styles.nav}>
      <div className={styles.logo}>
        {config.isDemo && <span className={styles.demoBadge}>DEMO</span>}
        {settings.logo ? (
          <Link to="/" className={styles.logoText}>
            <img src={settings.logo.url} alt={settings.siteName} className={styles.logoImage} />
          </Link>
        ) : (
          <Link to="/" className={styles.logoText}>
            {settings.siteName}
          </Link>
        )}
      </div>

      <button
        className={`${styles.menuToggle} ${mobileMenuOpen ? styles.menuToggleOpen : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation"
        aria-expanded={mobileMenuOpen}
      >
        <span className={styles.menuBar}></span>
        <span className={styles.menuBar}></span>
        <span className={styles.menuBar}></span>
      </button>

      <ul className={`${styles.navList} ${mobileMenuOpen ? styles.navListOpen : ''}`}>
        {navItems.map((item, index) => {
          const hasChildren = item.children.length > 0;
          const dropdownOpen = openDropdown === index;
          return (
            <li
              key={index}
              className={`${styles.navItem} ${hasChildren ? styles.navItemHasDropdown : ''} ${dropdownOpen ? styles.navItemDropdownOpen : ''}`}
              onMouseLeave={() => hasChildren && setOpenDropdown(prev => (prev === index ? null : prev))}
            >
              <span className={styles.navLinkWrap}>
                {isInternal(item.href) && !item.isExternal ? (
                  <Link
                    to={item.href}
                    className={styles.navLink}
                    onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href={item.href}
                    className={styles.navLink}
                    target={item.isExternal ? '_blank' : undefined}
                    rel={item.isExternal ? 'noopener noreferrer' : undefined}
                    onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}
                  >
                    {item.label}
                  </a>
                )}
                {hasChildren && (
                  <button
                    type="button"
                    className={styles.dropdownToggle}
                    aria-label={`${item.label} — rozwiń`}
                    aria-expanded={dropdownOpen}
                    onClick={() => setOpenDropdown(prev => (prev === index ? null : index))}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                  </button>
                )}
              </span>
              {hasChildren && (
                <ul className={styles.dropdownMenu}>
                  {item.children.map((child, ci) => (
                    <li key={ci} className={styles.dropdownItem}>
                      {isInternal(child.url) ? (
                        <Link
                          to={child.url}
                          className={styles.dropdownLink}
                          onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}
                        >
                          {child.label}
                        </Link>
                      ) : (
                        <a
                          href={child.url}
                          className={styles.dropdownLink}
                          onClick={() => { setMobileMenuOpen(false); setOpenDropdown(null); }}
                        >
                          {child.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
        {settings.showReserveButton && (
          <li className={styles.navItem}>
            <Link
              to="/rezerwacje"
              className={`${styles.navLink} ${styles.reserveButton}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {settings.reserveButtonText || t('nav.reserveOnline')}
            </Link>
          </li>
        )}
      </ul>

      <div className={styles.navActions}>
        {settings.features.shopEnabled && (
          <div className={styles.basketContainer} ref={basketRef}>
            <BasketIcon onClick={() => setBasketOpen(prev => !prev)} />
            <BasketPopup isOpen={basketOpen} onClose={() => setBasketOpen(false)} />
          </div>
        )}
        {settings.phoneButtonNumber && (
          <a
            href={`tel:${settings.phoneButtonNumber.replace(/\s/g, '')}`}
            className={`${styles.phoneButton} ${styles.phoneButtonDesktop}`}
            aria-label={settings.phoneButtonNumber}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L143.16,155A102,102,0,0,1,101,112.84l15.77-16.05,0,0a16,16,0,0,0,1.94-15.91l-21.11-47.11A16,16,0,0,0,82.59,24a16.14,16.14,0,0,0-5.18.86L38.19,39.51a16.11,16.11,0,0,0-11,14.07c-.11,1.35-.16,2.7-.16,4.06,0,103,83.66,186.7,186.69,186.7,1.36,0,2.71,0,4.06-.16a16.11,16.11,0,0,0,14.07-11l14.65-39.22A16.17,16.17,0,0,0,222.37,158.46Z"></path></svg>
          </a>
        )}
        {settings.showUserAvatar && (
          <div className={styles.userMenu} ref={userMenuRef}>
            <button
              className={styles.userMenuTrigger}
              aria-label="Konto"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <svg className={styles.userIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
            {userMenuOpen && (
              <div className={styles.userMenuDropdown}>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/moje-konto"
                      className={styles.userMenuItem}
                      onClick={() => { setUserMenuOpen(false); setMobileMenuOpen(false); }}
                    >
                      {t('nav.myAccount')}
                    </Link>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); setMobileMenuOpen(false); }}
                      className={styles.userMenuItem}
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/logowanie"
                    className={styles.userMenuItem}
                    onClick={() => { setUserMenuOpen(false); setMobileMenuOpen(false); }}
                  >
                    {t('nav.login')}
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    </header>
    {settings.phoneButtonNumber && (
      <a
        href={`tel:${settings.phoneButtonNumber.replace(/\s/g, '')}`}
        className={styles.phoneButtonFloating}
        aria-label={settings.phoneButtonNumber}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L143.16,155A102,102,0,0,1,101,112.84l15.77-16.05,0,0a16,16,0,0,0,1.94-15.91l-21.11-47.11A16,16,0,0,0,82.59,24a16.14,16.14,0,0,0-5.18.86L38.19,39.51a16.11,16.11,0,0,0-11,14.07c-.11,1.35-.16,2.7-.16,4.06,0,103,83.66,186.7,186.69,186.7,1.36,0,2.71,0,4.06-.16a16.11,16.11,0,0,0,14.07-11l14.65-39.22A16.17,16.17,0,0,0,222.37,158.46Z"></path></svg>
      </a>
    )}
    </>
  );
};
